import { createSlice } from '@reduxjs/toolkit';

// The default MaisonQR look. Must stay in sync with the merchant app's
// DEFAULT_CUSTOM_STORE_SETTINGS and the customer app's DEFAULT_STORE_THEME.
// The font entries are objects, not strings: the backend store schema types
// title_font_family/body_font_family as objects and rejects "" with "Please provide valid data".
export const DEFAULT_CUSTOM_STORE_SETTINGS = {
    store_logo: "",
    primary_color: "#9E2064",
    title_text_color: "#1f1f1f",
    body_text_color: "#666666",
    background_color: "#FFFFFF",
    title_font_family: {},
    body_font_family: {}
};

// Initial state
const initialState = {
    initial: { ...DEFAULT_CUSTOM_STORE_SETTINGS },
    custom: { ...DEFAULT_CUSTOM_STORE_SETTINGS },
    // Snapshot of what the server last returned. `initial` is the hardcoded defaults and must not
    // be used to decide whether the form has unsaved changes.
    saved: {
        settings: { ...DEFAULT_CUSTOM_STORE_SETTINGS },
        custom_store_theme: false,
        product_full_image_view: false
    },
    hasBaseline: false,
    past: [],
    future: [],
    // Value held aside while a single colour edit is in progress, so one edit records one undo
    // entry no matter how many change events the picker emits.
    pendingPast: null
};

// Create the slice
const customizeSlice = createSlice({
    name: 'customize',
    initialState,
    reducers: {
        updateState: (state, action) => {
            // Push the current state to the past stack
            state.past.push(state.custom);

            // Clear the future stack
            state.future = [];

            // Update the present state with the new state
            state.custom = { ...state.custom, ...action.payload };
        },
        /**
         * Apply an in-progress colour edit without recording undo history yet.
         *
         * The colour picker emits a change event for every pointer move and for every keystroke in
         * its hex field. Recording each one made a single Undo click imperceptible; recording none
         * of them (by listening only for the picker's "complete" event) dropped hex-field edits
         * entirely, because that event never fires for the inputs. So the value is applied on every
         * event and the history entry is held back until commitState.
         */
        previewState: (state, action) => {
            if (state.pendingPast === null) {
                state.pendingPast = state.custom;
            }
            state.future = [];
            state.custom = { ...state.custom, ...action.payload };
        },
        /** Close off an in-progress edit, recording it as a single undo entry. */
        commitState: (state) => {
            if (state.pendingPast !== null) {
                state.past.push(state.pendingPast);
                state.pendingPast = null;
            }
        },
        undo: (state) => {
            // An edit still in progress counts as the most recent change, so record it before
            // stepping back. Discarding it meant that after typing into the hex field — which
            // never fires the picker's "complete" event — Undo either did nothing or skipped
            // over that edit to an older one.
            if (state.pendingPast !== null) {
                state.past.push(state.pendingPast);
                state.pendingPast = null;
            }
            if (state.past.length > 0) {
                // Push the current state to the future stack
                state.future.push(state.custom);

                // Pop the last state from the past stack and set it as the present state
                state.custom = state.past.pop();
            }
        },
        redo: (state) => {
            if (state.pendingPast !== null) {
                state.past.push(state.pendingPast);
                state.pendingPast = null;
            }
            if (state.future.length > 0) {
                // Push the current state to the past stack
                state.past.push(state.custom);

                // Pop the last state from the future stack and set it as the present state
                state.custom = state.future.pop();
            }
        },
        /**
         * Seed the form from what the API returned.
         *
         * Replaces the draft outright instead of merging: merging left the previously viewed
         * store's colours in place for any key the newly opened store had not saved, so one
         * store's palette showed up on — and could be saved onto — another.
         *
         * Also clears the undo history, so Undo can no longer walk backwards past the store's
         * saved values into the hardcoded defaults.
         */
        loadSettings: (state, action) => {
            const {
                settings,
                custom_store_theme = false,
                product_full_image_view = false
            } = action.payload || {};

            const resolved = { ...DEFAULT_CUSTOM_STORE_SETTINGS, ...(settings || {}) };
            // product_full_image_view is a store-level toggle, not part of the palette.
            delete resolved.product_full_image_view;

            state.custom = resolved;
            state.saved = {
                settings: resolved,
                custom_store_theme: Boolean(custom_store_theme),
                product_full_image_view: Boolean(product_full_image_view)
            };
            state.hasBaseline = true;
            state.past = [];
            state.future = [];
            state.pendingPast = null;
        },
        setInitial: (state) => {
            state.custom = initialState.initial;
            state.saved = initialState.saved;
            state.hasBaseline = false;
            state.past = [];
            state.future = [];
            state.pendingPast = null;
        }
    }
});

// Export actions and reducer
export const { updateState, previewState, commitState, undo, redo, setInitial, loadSettings } = customizeSlice.actions;
export default customizeSlice.reducer;
